/**
 * The whole range, at a glance.
 *
 * One decision at a time teaches you that hand. The grid teaches you the SHAPE — that
 * the range is every pair and every ace and then a suited gap, and where its edge is.
 * That edge is the only part worth memorising, and it is invisible from a stream of
 * single hands.
 *
 * It is drawn from `node.strategy`, which the server already sent in full for a spot
 * this account paid a spot for. Nothing here asks for anything more, and nothing here
 * is gated: the numbers are already in the client's hands, so a lock on the picture
 * would be decoration.
 */
import { HAND_GRID, band, combos, playFreq, mixFor, rangeWidth, wholePercents } from "../lib/range";
import type { ChartNode } from "../lib/api";

export function RangeGrid({ node, dealtCode, revealed }: {
  node: ChartNode;
  /** The hand in front of the player, outlined so they can find it. */
  dealtCode: string | null;
  /**
   * Whether the answer is allowed to be on screen.
   *
   * The grid IS the answer. Showing it while somebody is still deciding turns every
   * drill into a reading exercise, so it stays covered until the hand is graded —
   * the same reason the verdict replaces the action buttons rather than sitting
   * beside them.
   */
  revealed: boolean;
}) {
  const actions = node.actions;
  const play = actions.find((a) => a.id !== "fold");

  /**
   * The label describes the SHAPE, it does not read out the range.
   *
   * It used to list every hand: "always: AA, AKs, AQs, …" — well over a hundred
   * comma-separated codes in one uninterruptible label. A sighted player takes the
   * shape of this grid in at a glance, and the equivalent for a screen reader is a
   * width and a boundary, not a recital. Anyone who wants a specific hand can reach
   * it: every cell carries its own exact mix in its title.
   */
  const summary = (() => {
    if (!revealed) return "The equilibrium range, covered until this hand is graded.";
    let pure = 0, mixed = 0, played = 0, total = 0;
    for (const h of HAND_GRID) {
      const f = playFreq(node.strategy, h.code, actions);
      if (f === null) continue;
      const w = combos(h.code);
      total += w; played += w * f;
      const b = band(f);
      if (b === "pure") pure++;
      else if (b === "mix") mixed++;
    }
    if (total === 0) return `This chart carries no range for the ${node.actor}.`;
    const width = rangeWidth(node.strategy, actions);
    return `${play?.label ?? "Play"} range for the ${node.actor}: ` +
      `${width === null ? "" : `${(width * 100).toFixed(1)} per cent of hands dealt, `}` +
      `${pure} of the 169 hands always, ${mixed} some of the time. ` +
      `Percentage: ${((played / total) * 100).toFixed(1)}.`;
  })();

  return (
    <div className={`rangegrid ${revealed ? "" : "rangegrid-hidden"}`}>
      <div className="rg-wrap">
      <div className="rg-grid" role="img" aria-label={summary}>
        {HAND_GRID.map((h) => {
          const f = revealed ? playFreq(node.strategy, h.code, actions) : null;
          const b = revealed ? band(f) : "uncovered";
          const mix = revealed ? mixFor(node.strategy, h.code, actions.length) : null;
          /*
           * The title carries the exact mix. A cell is 22px and cannot hold four
           * numbers, but a player who wants to know whether 66 is 70/30 or 30/70
           * should not have to deal 66 twenty times to find out.
           */
          const whole = mix ? wholePercents(mix) : null;
          const title = whole
            ? `${h.code} — ${actions.map((a, i) => `${a.label} ${whole[i] ?? 0}%`).join(", ")}`
            : h.code;
          return (
            <span key={h.code} title={title}
                  className={`rg-cell rg-${b} ${h.code === dealtCode ? "rg-you" : ""}`}
                  /*
                   * A partial fill rather than a flat tint for the mixed band: the
                   * height of the fill IS the frequency, so a 20% mix and an 80% mix
                   * are distinguishable without reading the tooltip. Pure and never
                   * do not need it and are drawn flat.
                   */
                  style={b === "mix" && f !== null
                    ? { ["--rg-f" as string]: `${Math.round(f * 100)}%` }
                    : undefined}>
              {h.code}
            </span>
          );
        })}
      </div>
      {/*
        * A covering panel rather than a blur.
        *
        * The first version blurred the real grid, which is not a cover — it is the
        * answer at low resolution, and the shape of a shove range is exactly the kind
        * of thing that survives a blur. It also read as a rendering fault: a box of
        * grey mush with no explanation is indistinguishable from a component that
        * failed to load.
        */}
      {!revealed && (
        <p className="rg-cover">Covered until this hand is graded</p>
      )}
      </div>

      <ul className="rg-key">
        <li><i className="rg-swatch rg-pure" />{play?.label ?? "Play"}</li>
        <li><i className="rg-swatch rg-mix" />Mixed</li>
        <li><i className="rg-swatch rg-never" />Fold</li>
      </ul>
    </div>
  );
}
